/**
 * TrackingProvider
 * Sprint 7 - Servicio global de tracking de ubicación
 *
 * Componente que inicia el tracking de ubicación para TODOS los usuarios
 * autenticados (admin y normales). Se monta una vez dentro de las rutas
 * protegidas y mantiene el tracking activo mientras el usuario esté
 * logueado, sin importar en qué página se encuentre.
 *
 * Flujo:
 * 1. Al montar, verifica permisos de ubicación
 * 2. Si no tiene permisos y no está en modo manual, solicita al usuario
 * 3. Inicia o detiene el watch continuo según permisos y modo manual
 * 4. Al desmontar (logout), detiene el tracking
 */

import { useEffect, useRef } from 'react';
import { useLocationStore, useSystemToolsStore } from '../../stores';
import { useManualLocationStore } from '../../stores/manualLocationStore';
import { useManualLocationSync } from '../../hooks/useManualLocationSync';

export function TrackingProvider({ children }: { children: React.ReactNode }) {
    useManualLocationSync();

    const {
        permissionStatus,
        isWatching,
        checkPermissions,
        requestPermissions,
        startWatching,
        stopWatching,
    } = useLocationStore();
    const manualModeEnabled = useManualLocationStore((state) => state.manualModeEnabled);
    const systemToolSettings = useSystemToolsStore((state) => state.settings);
    const loadSystemToolSettings = useSystemToolsStore((state) => state.load);
    const manualModeActive = systemToolSettings?.manual_location_enabled === true && manualModeEnabled;
    const settingsLoaded = systemToolSettings !== null;

    // Evitar doble inicialización en StrictMode
    const initializedRef = useRef(false);

    useEffect(() => {
        void loadSystemToolSettings();
    }, [loadSystemToolSettings]);

    // Paso 1: Verificar permisos al montar
    useEffect(() => {
        checkPermissions();
    }, [checkPermissions]);

    // Paso 2: Solicitar permisos si están en 'prompt'
    useEffect(() => {
        if (!settingsLoaded || manualModeActive) {
            return;
        }

        if (permissionStatus === 'prompt' && !initializedRef.current) {
            initializedRef.current = true;
            requestPermissions();
        }
    }, [manualModeActive, permissionStatus, requestPermissions, settingsLoaded]);

    // Paso 3: Iniciar o detener tracking según el modo manual
    useEffect(() => {
        if (!settingsLoaded) {
            return;
        }

        if (manualModeActive) {
            if (isWatching) {
                void stopWatching();
            }
            return;
        }

        if (permissionStatus === 'granted' && !isWatching) {
            startWatching(true); // true = sync al servidor
        }
    }, [manualModeActive, permissionStatus, isWatching, settingsLoaded, startWatching, stopWatching]);

    // Paso 4: Cleanup al desmontar (usuario hace logout)
    useEffect(() => {
        return () => {
            stopWatching();
            initializedRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <>{children}</>;
}
